import type { ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Download, Linkedin } from 'lucide-react';
import {
  certificateVerifyUrl,
  linkedInAddToProfileUrl,
  useApi,
  type Certificate,
  type CertificateFileFormat,
} from '@/lib/api';
import { LINKEDIN_TAG_TIP, linkedInPostText } from '@/lib/linkedin';
import { LinkedInShareButton, NativeShareButton } from './share-buttons';
import { Button, ErrorText } from './ui';

/** Downloads the signed-in learner's certificate (rendered on demand), public or private. */
export function CertificateDownloadButton({
  code,
  format = 'pdf',
  variant = 'secondary',
  className = '',
}: {
  code: string;
  format?: CertificateFileFormat;
  variant?: 'primary' | 'secondary' | 'ghost';
  className?: string;
}) {
  const api = useApi();
  const download = useMutation({ mutationFn: () => api.downloadCertificate(code, format) });
  return (
    <div className={className}>
      <Button
        variant={variant}
        className="w-full"
        disabled={download.isPending}
        onClick={() => download.mutate()}
      >
        <Download className="size-4" aria-hidden />
        {download.isPending ? 'Preparing…' : `Download ${format.toUpperCase()}`}
      </Button>
      <ErrorText error={download.error} />
    </div>
  );
}

const linkClass = {
  primary: 'bg-primary text-primary-foreground hover:opacity-90',
  secondary: 'border border-border bg-card text-foreground hover:bg-muted',
};

function ExternalLink({
  href,
  variant,
  children,
}: {
  href: string;
  variant: keyof typeof linkClass;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center justify-center gap-2 rounded-[var(--radius)] px-4 py-2 text-sm font-medium transition-colors ${linkClass[variant]}`}
    >
      {children}
    </a>
  );
}

/**
 * The owner's LinkedIn actions for a public certificate: post it with a
 * ready-made message tagging Nirvik AI Labs (LinkedIn shows the preview image
 * from the verify page), or add it to their profile.
 */
export function LinkedInActions({
  cert,
  kind,
}: {
  cert: Pick<Certificate, 'code' | 'title' | 'issuedAt' | 'timeZone'>;
  /** What it's for: shapes the post's wording. */
  kind: 'quiz' | 'course' | 'event';
}) {
  const post = linkedInPostText({
    kind: 'certificate',
    of: kind,
    title: cert.title,
    url: certificateVerifyUrl(cert.code),
  });
  // Side by side only when there's room in its own column (it also sits in narrow cards).
  return (
    <div className="@container">
      <div className="grid gap-3 @lg:grid-cols-2">
        <LinkedInShareButton post={post} url={certificateVerifyUrl(cert.code)} />
        <NativeShareButton title={cert.title} text={post} />
        <ExternalLink href={linkedInAddToProfileUrl(cert)} variant="secondary">
          <Linkedin className="size-4" aria-hidden /> Add Certificate to LinkedIn Profile
        </ExternalLink>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{LINKEDIN_TAG_TIP}</p>
    </div>
  );
}
